import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Settings as SettingsIcon, Check, Mail } from "lucide-react";

/**
 * User settings — Affynix is email-only by design (no calendar/booking links).
 * The only editable profile field is the user's display name; the contact email
 * is fixed at admin@affynix.it and is exposed automatically on public pitches.
 */
export default function SettingsPage() {
    const { user, setUser } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        setName(user?.name || "");
    }, [user?.name]);

    const dirty = name !== (user?.name || "");

    const save = async () => {
        setSaving(true); setErr(""); setSaved(false);
        try {
            const { data } = await api.patch("/auth/me", { name: name.trim() });
            // Keep the auth context in sync so AI prompts pick up the change.
            if (setUser) setUser((u) => ({ ...u, ...data }));
            setSaved(true);
            setTimeout(() => setSaved(false), 2200);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto p-8 space-y-8" data-testid="settings-page">
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Settings</div>
                <h1 className="font-heading font-black text-4xl text-zinc-50 tracking-tight">Profile</h1>
                <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                    Affynix is strictly email-based: every prospect gets a direct email path on every shared pitch. No calendars, no booking links, no calls.
                </p>
            </div>

            {/* Name */}
            <section className="border border-zinc-800 bg-zinc-950 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Display name</div>
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="settings-name-input"
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 outline-none text-zinc-100 text-base px-3 py-2"
                />
            </section>

            {/* Contact email — read-only info card */}
            <section className="border border-zinc-800 bg-zinc-950 p-6" data-testid="settings-contact-section">
                <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1">Email-only outreach</div>
                        <h2 className="font-heading font-bold text-lg text-zinc-50 leading-tight">Public contact: admin@affynix.it</h2>
                        <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                            Every shared pitch and AI-drafted follow-up routes prospects to this single email inbox. Replies live in your inbox — no meeting tag, no calendar dance.
                        </p>
                    </div>
                </div>
            </section>

            {/* Save */}
            <div className="flex items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving || !dirty}
                    data-testid="settings-save-btn"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5"
                >
                    {saved ? <><Check className="w-3.5 h-3.5" /> Saved</> : <><SettingsIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}</>}
                </button>
                {err && <span className="text-xs text-red-400 font-mono" data-testid="settings-error">{err}</span>}
            </div>
        </div>
    );
}
